import { Code2, LogIn, Shield, ArrowRight, Sparkles, FileText, Headphones, FolderOpen, CreditCard, Bell, BarChart, BookOpen, MessageSquare, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { login } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";

const ClientPortal = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const features = [
    {
      icon: Headphones,
      title: "Suporte Técnico",
      description: "Abra tickets e acompanhe suas solicitações em tempo real"
    },
    {
      icon: FolderOpen,
      title: "Meus Projetos",
      description: "Acesse documentação, status e informações dos seus projetos"
    },
    {
      icon: CreditCard,
      title: "Faturas",
      description: "Consulte histórico de pagamentos e faturas pendentes"
    },
    {
      icon: BarChart,
      title: "Relatórios",
      description: "Visualize métricas de performance e uptime dos serviços"
    },
    {
      icon: BookOpen,
      title: "Base de Conhecimento",
      description: "Tutoriais, guias e documentação técnica"
    },
    {
      icon: Bell,
      title: "Notificações",
      description: "Fique por dentro de atualizações e manutenções"
    }
  ];

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await login(email, password);

      toast({
        title: 'Login realizado com sucesso',
        description: `Bem-vindo, ${response.user.full_name}!`,
      });

      console.log('=== LOGIN REDIRECT DEBUG ===');
      console.log('User type:', response.user.user_type);
      console.log('Role ID:', response.user.role_id);
      console.log('Is support?', response.user.user_type === 'internal' && response.user.role_id === 4);

      // Redirect support users to admin tickets page
      if (response.user.user_type === 'internal' && response.user.role_id === 4) {
        console.log('Redirecting to: /cliente/admin/tickets');
        navigate('/cliente/admin/tickets');
      } else {
        console.log('Redirecting to: /cliente/dashboard');
        navigate('/cliente/dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao fazer login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Vertical lines pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.3)_1px,transparent_1px)] bg-[size:8rem_100%] pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', WebkitMaskComposite: 'source-in' }} />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
          </Link>
          <nav className="hidden md:flex gap-6">
            <Link to="/#servicos" className="text-muted-foreground hover:text-primary transition-colors">
              Serviços
            </Link>
            <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">
              Blog
            </Link>
            <Link to="/seguranca-digital" className="text-muted-foreground hover:text-primary transition-colors">
              Segurança Digital
            </Link>
            <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
              Contato
            </Link>
          </nav>
          <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
            <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-32 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Info */}
            <div className="space-y-8">
              <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
                <ArrowRight className="h-4 w-4 rotate-180" />
                Voltar para Home
              </Link>

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6 backdrop-blur-sm">
                <Sparkles className="h-4 w-4 text-primary animate-pulse" />
                <span className="text-sm font-medium text-primary">Área Exclusiva</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                Portal do{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
                  Cliente
                </span>
              </h1>

              <p className="text-xl text-muted-foreground mb-8">
                Acesse sua área exclusiva para gerenciar projetos, abrir tickets de suporte e muito mais.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Acesso Seguro</h3>
                    <p className="text-sm text-muted-foreground">Seus dados protegidos com criptografia de ponta a ponta</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MessageSquare className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Suporte 24/7</h3>
                    <p className="text-sm text-muted-foreground">Equipe disponível para atender suas necessidades</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Login Form */}
            <Card className="border-border/50 bg-card/50 backdrop-blur-xl">
              <CardHeader className="text-center">
                <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-4">
                  <LogIn className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl">Acesse sua conta</CardTitle>
                <CardDescription>
                  Entre com suas credenciais para acessar o portal
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-background/50 border-border/50 focus:border-primary transition-colors"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Senha</Label>
                      <Link to="/recuperar-senha" className="text-sm text-primary hover:underline">
                        Esqueceu a senha?
                      </Link>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-background/50 border-border/50 focus:border-primary transition-colors"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full gap-2 group"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Entrando...
                      </>
                    ) : (
                      <>
                        Entrar
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>

                  <div className="text-center text-sm text-muted-foreground">
                    Não tem acesso?{" "}
                    <Link to="/contato" className="text-primary hover:underline">
                      Entre em contato
                    </Link>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 relative bg-card/30">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              O que você encontra no portal
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Tudo que você precisa em um só lugar
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={index}
                  className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group"
                >
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-lg text-foreground">{feature.title}</CardTitle>
                    <CardDescription className="text-sm text-muted-foreground">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10 max-w-4xl">
          <Card className="bg-gradient-to-br from-primary/20 via-card/50 to-accent/20 border-primary/30 backdrop-blur-xl overflow-hidden relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
            <CardHeader className="text-center pb-6 relative z-10">
              <CardTitle className="text-3xl md:text-4xl mb-4 text-foreground">
                Precisa de ajuda?
              </CardTitle>
              <CardDescription className="text-foreground/80 text-lg">
                Nossa equipe está pronta para atender você
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center relative z-10">
              <Button size="lg" asChild variant="outline" className="gap-2 border-primary/50 hover:bg-primary/10">
                <Link to="/contato">
                  <MessageSquare className="h-5 w-5" />
                  Falar com Suporte
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ClientPortal;
